import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Seo } from "@/components/Seo";
import { SITE_URL } from "@/lib/siteConfig";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Coins, Zap, Trophy, Lock, Check, ArrowRight, Waves, Wind, Sun } from "lucide-react";

const TIER_STYLE = {
  bronze: { color: "#E08D3C", shadow: "shadow-[6px_6px_0px_#E08D3C]", ring: "border-[#E08D3C]/40", icon: Coins },
  silver: { color: "#C7D2DE", shadow: "shadow-[6px_6px_0px_#C7D2DE]", ring: "border-[#C7D2DE]/40", icon: Zap },
  gold:   { color: "#F5C842", shadow: "shadow-[6px_6px_0px_#F5C842]", ring: "border-[#F5C842]/60", icon: Trophy },
};

const ALIGN_WORLDS = [
  { id: "stillness", name: "The Stillness", tagline: "Rest the restless mind", color: "#2DD4BF", icon: Waves,
    blurb: "A quiet teal expanse to slow down, breathe, and return to the present. Free to enter." },
  { id: "insight", name: "Hall of Insight", tagline: "See clearly, choose wisely", color: "#A78BFA", icon: Wind,
    blurb: "A violet chamber of stars where reflection ripens into understanding. Silver & Gold." },
  { id: "radiance", name: "Path of Radiance", tagline: "Live your light", color: "#F5C842", icon: Sun,
    blurb: "A golden path where insight becomes mindful, purposeful living. Gold only." },
];

const SPIRITUAL_LINKS = {
  bronze: [{ label: "Alignment", to: "/alignment" }, { label: "Mindful Games", to: "/mindful" }, { label: "Journey", to: "/journey" }],
  silver: [{ label: "Alignment", to: "/alignment" }, { label: "Mindful Games", to: "/mindful" }, { label: "Breathing", to: "/alignment" }, { label: "Journey", to: "/journey" }],
  gold: [{ label: "Alignment", to: "/alignment" }, { label: "Mindful Games", to: "/mindful" }, { label: "Insights", to: "/journey" }, { label: "Journey", to: "/journey" }],
};

export default function Landing() {
  const { user } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [pricing, setPricing] = useState({});

  useEffect(() => {
    api.get("/tiers").then((r) => setTiers(r.data.tiers));
    api.get("/pricing").then((r) => setPricing(r.data.tiers || {})).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A14] text-slate-200">
      <Seo
        title="InnerArcade — Free Mindful Browser Games, Daily Challenges & Rewards"
        description="Play quick free browser games, complete daily challenges, build a practice streak, and earn rewards while you play. Trivia, breathing and casual games — no download."
        path="/"
        keywords="free browser games, mindful games, daily challenge games, trivia game, breathing game, play games online, arcade games with rewards"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "Organization", name: "InnerArcade", url: SITE_URL, description: "A mindful browser arcade — play with purpose." },
            { "@type": "WebSite", name: "InnerArcade", url: SITE_URL, potentialAction: { "@type": "SearchAction", target: `${SITE_URL}/market?q={search_term_string}`, "query-input": "required name=search_term_string" } },
          ],
        }}
      />
      <Header />

      {/* TOP: Alignment + Spiritual Worlds, with the three tiers next to it */}
      <section id="tiers" className="relative overflow-hidden border-b border-violet-500/20">
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(900px 500px at 15% 0%, rgba(88,28,135,0.4), transparent 60%), radial-gradient(800px 600px at 95% 20%, rgba(14,116,144,0.3), transparent 60%), #08060f" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 py-16">
          <h1 className="font-head font-900 text-3xl sm:text-4xl lg:text-5xl leading-[0.95] text-slate-50 mb-3 max-w-3xl">
            LEARN WISDOM. BUILD ALIGNMENT. <span className="text-[#2DD4BF]">PLAY WITH PURPOSE.</span>
          </h1>
          <p className="text-slate-400 max-w-2xl mb-12 text-base">
            Play quick <span className="text-[#2DD4BF]">free browser games</span>, complete daily challenges, and earn Journey XP — plus <span className="text-[#A78BFA]">Alignment</span>, a calm space of mindful practice across three themed worlds.
          </p>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Alignment · Spiritual Worlds */}
            <div data-testid="landing-alignment">
              <h2 className="font-head font-700 text-2xl text-slate-50 mb-1">Alignment · Spiritual Worlds</h2>
              <p className="text-sm text-slate-400 mb-6">Reflect with an AI guide, breathe, and build mindful habits. Your plan unlocks deeper practice.</p>
              <div className="space-y-4 mb-6">
                {ALIGN_WORLDS.map((w) => {
                  const Icon = w.icon;
                  return (
                    <div key={w.id} data-testid={`landing-world-${w.id}`} className="flex items-start gap-4 rounded-2xl border p-5 bg-white/[0.03] backdrop-blur-sm" style={{ borderColor: `${w.color}33` }}>
                      <Icon className="w-8 h-8 shrink-0" style={{ color: w.color }} />
                      <div>
                        <h3 className="font-head font-700 text-lg text-slate-50">{w.name}</h3>
                        <p className="text-xs uppercase tracking-wider mb-1" style={{ color: w.color }}>{w.tagline}</p>
                        <p className="text-sm text-slate-400 leading-relaxed">{w.blurb}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Link
                to={user ? "/alignment" : "/auth"}
                data-testid="alignment-cta-btn"
                className="inline-flex items-center gap-2 font-arcade text-xl px-8 py-3 bg-[#A78BFA] text-black border border-[#A78BFA] hover:shadow-[6px_6px_0px_#2DD4BF] hover:-translate-x-1 hover:-translate-y-1 transition-transform duration-200"
              >
                {user ? "ENTER ALIGNMENT" : "BEGIN YOUR JOURNEY"} <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            {/* Three tiers */}
            <div data-testid="landing-tiers">
              <h2 className="font-head font-700 text-2xl text-slate-50 mb-1">Choose your plan</h2>
              <p className="text-sm text-slate-400 mb-6">Three tiers, one journey. Free to start.</p>
              <div className="space-y-4">
                {tiers.map((t) => {
                  const s = TIER_STYLE[t.id];
                  const Icon = s.icon;
                  const featured = t.id === "gold";
                  return (
                    <div
                      key={t.id}
                      data-testid={`tier-card-${t.id}`}
                      className={`relative rounded-2xl bg-[#11111A]/80 backdrop-blur-sm border ${s.ring} p-5 transition-transform duration-200 hover:-translate-y-0.5`}
                    >
                      {featured && (
                        <span className="absolute -top-3 right-5 font-arcade text-sm bg-[#F5C842] text-black px-3 py-0.5">BEST VALUE</span>
                      )}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Icon className="w-6 h-6" style={{ color: s.color }} />
                          <h3 className="font-head font-900 text-xl text-slate-50">{t.name}</h3>
                        </div>
                        <span className="font-arcade text-sm" style={{ color: s.color }}>{t.tagline}</span>
                      </div>
                      <div className="mb-3">
                        {t.free ? (
                          <span className="font-head font-900 text-2xl" style={{ color: s.color }}>FREE</span>
                        ) : (
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="font-head font-700 text-2xl" style={{ color: s.color }}>{t.price_btc}</span>
                            <span className="font-arcade text-base text-slate-400">BTC</span>
                            {pricing[t.id]?.usd != null && (
                              <span className="text-base text-slate-300" data-testid={`tier-usd-${t.id}`}>≈ ${pricing[t.id].usd}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <ul className="space-y-1.5 mb-3">
                        {t.perks.map((p) => (
                          <li key={p} className="flex items-start gap-2 text-sm text-slate-300">
                            <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: s.color }} /> {p}
                          </li>
                        ))}
                      </ul>
                      <div className="flex flex-wrap gap-1.5 mb-4" data-testid={`tier-links-${t.id}`}>
                        {SPIRITUAL_LINKS[t.id].map((l) => (
                          <Link
                            key={l.label}
                            to={user ? l.to : "/auth"}
                            data-testid={`tier-${t.id}-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="text-xs px-2.5 py-1 rounded-full border transition-colors duration-200 hover:bg-white/5"
                            style={{ borderColor: `${s.color}55`, color: s.color }}
                          >
                            {l.label}
                          </Link>
                        ))}
                      </div>
                      <Link
                        to={user ? (t.free ? "/games" : `/pay/${t.id}`) : "/auth"}
                        data-testid={`tier-select-${t.id}`}
                        className="block text-center font-arcade text-lg py-2.5 border transition-transform duration-200 hover:-translate-y-0.5"
                        style={{ background: s.color, color: "#0A0A14", borderColor: s.color }}
                      >
                        {!user ? "SIGN UP" : t.free ? "PLAY FREE" : "SUBSCRIBE"}
                      </Link>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* marquee */}
        <div className="relative border-t border-violet-500/20 bg-[#11111A] py-3 overflow-hidden">
          <div className="marquee-track font-arcade text-xl text-[#F5C842]">
            {["marquee-a", "marquee-b"].map((mid) => (
              <span key={mid} className="px-6">LEARN WISDOM ★ BREATHE ★ REFLECT ★ PLAY WITH PURPOSE ★ BUILD ALIGNMENT ★ CHASE HIGH SCORES ★ </span>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-t border-violet-500/20 bg-[#11111A]">
        <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-10">
          {[
            { n: "01", t: "Pick a tier", d: "Bronze, Silver or Gold — each unlocks more games and deeper Alignment practice.", i: Coins },
            { n: "02", t: "Start your practice", d: "Daily reflections, breathing, and mindful games — build habits and earn Journey XP.", i: Zap },
            { n: "03", t: "Grow with purpose", d: "Track your streaks and badges as you learn, reflect, and make more conscious choices.", i: Lock },
          ].map((step) => {
            const Icon = step.i;
            return (
              <div key={step.n} className="relative">
                <span className="font-head font-900 text-6xl text-violet-500/10 absolute -top-6 -left-2">{step.n}</span>
                <Icon className="w-8 h-8 text-[#A78BFA] relative mb-4" />
                <h3 className="font-head font-700 text-xl text-slate-50 mb-2 relative">{step.t}</h3>
                <p className="text-sm text-slate-400 relative">{step.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      <footer className="border-t border-violet-500/20 py-8 text-center">
        <p className="font-arcade text-lg text-slate-500">INNERARCADE ★ PLAY WITH PURPOSE ★ {new Date().getFullYear()}</p>
      </footer>
    </div>
  );
}
